import React, { useState, useEffect } from 'react';
import { Button, message, Progress } from 'antd';
import { Download, Upload, AlertCircle, X, ArrowLeft, CheckCircle, File, FileSpreadsheet, Folder, Zap } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from '../../services/axios';
import { useNavigate } from 'react-router-dom';

const BulkUpload = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [excelFiles, setExcelFiles] = useState([]);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [excelDragActive, setExcelDragActive] = useState(false);
  const [documentDragActive, setDocumentDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadErrors, setUploadErrors] = useState([]);
  const user = localStorage.getItem('user');
  const role = JSON.parse(localStorage.getItem('role'))[0];
  const disciplines = [
    "Architectural", "BMS", "CCTV", "Coordination", "Civil", "Electrical",
    "Finishes", "Fire Fighting", "Fire Alarm", "General", "HVAC", "IT",
    "ID Works", "Mechanical", "Plumbing", "Provisional Sum Items", "Structural", "Telecom", "General"
  ];

  const steps = [
    { id: 1, title: 'Download Template', icon: Download, status: 'completed' },
    { id: 2, title: 'Upload Excel', icon: FileSpreadsheet, status: 'current' },
    { id: 3, title: 'Upload Documents', icon: Folder, status: 'pending' },
    { id: 4, title: 'Review & Submit', icon: CheckCircle, status: 'pending' }
  ];

  useEffect(() => {
    getUserDetails();
  }, []);

  const getUserDetails = async () => {
    try {
      const res = await axios.post('get-user-details', { user });
      if (res?.data?.userDetails) {
        if (res?.data?.userDetails.documentType) {
          setDocumentTypes(res.data.userDetails.documentType);
        }
        if (res?.data?.userDetails.documentCategory) {
          setDocumentCategories(res.data.userDetails.documentCategory);
        }
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      if (err.response && err.response.data && err.response.data.msg) {
        setErrors(prev => ({ ...prev, general: err.response.data.msg }));
      } else if (err.response && err.response.data && err.response.data.userName) {
        setErrors(prev => ({ ...prev, general: err.response.data.userName[0] }));
      } else if (err.response && err.response.status === 401) {
        setErrors(prev => ({ ...prev, general: 'User not authorized' }));
      } else {
        setErrors(prev => ({ ...prev, general: 'Something went wrong' }));
      }
    }
  };

  const downloadExcelTemplate = () => {
    try {
      const headers = [
        'Document Type',
        'Category',
        'Discipline',
        'Description',
        'Document Reference'
      ];
      const sampleData = [
        {
          'Document Type': `Valid values: ${documentTypes.length > 0 ? documentTypes.join(', ') : 'None available'}`,
          'Category': `Valid values: ${documentCategories.length > 0 ? documentCategories.join(', ') : 'None available'}`,
          'Discipline': `Valid values: ${disciplines.join(', ')}`,
          'Description': 'Enter description here',
          'Document Reference': 'Enter reference number (must match uploaded file name without extension, e.g., "transmittal" for "transmittal.pdf")'
        }
      ];
      const ws = XLSX.utils.json_to_sheet(sampleData, { header: headers, skipHeader: false });
      ws['!cols'] = [
        { wch: 40 },
        { wch: 40 },
        { wch: 40 },
        { wch: 40 },
        { wch: 30 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bulk Upload Template');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      saveAs(blob, 'bulk_upload_template.xlsx');
      message.success('Excel template downloaded successfully!');
    } catch (err) {
      message.error('Failed to download template');
      console.error('Download Error:', err);
    }
  };

  const handleExcelFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Math.random().toString(36).substr(2, 9)
    }));
    setExcelFiles(newFiles.slice(0, 1)); // Allow only one Excel file
    if (errors.excelFile) {
      setErrors(prev => ({ ...prev, excelFile: '' }));
    }
    setCurrentStep(2);
  };

  const handleDocumentFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Math.random().toString(36).substr(2, 9)
    }));
    setDocumentFiles(newFiles); // Allow multiple document files
    if (errors.documentFiles) {
      setErrors(prev => ({ ...prev, documentFiles: '' }));
    }
    setCurrentStep(3);
  };

  const handleDragEvents = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      if (type === 'excel') setExcelDragActive(true);
      else setDocumentDragActive(true);
    } else if (e.type === "dragleave") {
      if (type === 'excel') setExcelDragActive(false);
      else setDocumentDragActive(false);
    }
  };

  const handleFileDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'excel') setExcelDragActive(false);
    else setDocumentDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (type === 'excel') {
        handleExcelFiles(e.dataTransfer.files);
      } else {
        handleDocumentFiles(e.dataTransfer.files);
      }
    }
  };

  const removeExcelFile = (id) => {
    setExcelFiles(excelFiles.filter(file => file.id !== id));
    setCurrentStep(2);
  };

  const removeDocumentFile = (id) => {
    setDocumentFiles(documentFiles.filter(file => file.id !== id));
    setCurrentStep(3);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateExcelData = (data, documentFileNames) => {
    const requiredHeaders = ['Document Type', 'Category', 'Discipline', 'Description', 'Document Reference'];
    const headers = Object.keys(data[0] || {});
    
    const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
    if (missingHeaders.length > 0) {
      return `Missing required headers: ${missingHeaders.join(', ')}`;
    }

    const validData = data.filter(row => 
      !row['Document Type']?.startsWith('Valid values:') &&
      !row['Category']?.startsWith('Valid values:') &&
      !row['Discipline']?.startsWith('Valid values:') &&
      row['Description'] !== 'Enter description here' &&
      row['Document Reference'] !== 'Enter reference number (must match uploaded file name without extension, e.g., "transmittal" for "transmittal.pdf")'
    );

    const documentFileNamesWithoutExt = documentFileNames.map(name =>
      name.replace(/\.[^/.]+$/, '').trim().toLowerCase()
    );

    if (validData.length !== documentFileNames.length) {
      return `The number of documents in the Excel file (${validData.length}) does not match the number of uploaded files (${documentFileNames.length})`;
    }

    for (let i = 0; i < validData.length; i++) {
      const row = validData[i];
      if (!row['Document Type'] || !documentTypes.includes(row['Document Type'])) {
        return `Invalid or missing Document Type in row ${i + 3}`;
      }
      if (!row['Category'] || !documentCategories.includes(row['Category'])) {
        return `Invalid or missing Category in row ${i + 3}`;
      }
      if (!row['Discipline'] || !disciplines.includes(row['Discipline'])) {
        return `Invalid or missing Discipline in row ${i + 3}`;
      }
      if (!row['Description']) {
        return `Missing Description in row ${i + 3}`;
      }
      if (!row['Document Reference']) {
        return `Missing Document Reference in row ${i + 3}`;
      }
      const docRefWithoutExt = row['Document Reference'].replace(/\.[^/.]+$/, '').trim().toLowerCase();
      if (!documentFileNamesWithoutExt.includes(docRefWithoutExt)) {
        return `Document Reference "${row['Document Reference']}" in row ${i + 3} does not match any uploaded file`;
      }
    }

    return null;
  };

  const handleUpload = async () => {
    if (excelFiles.length === 0) {
      setErrors(prev => ({ ...prev, excelFile: 'Please select an Excel file to upload' }));
      return;
    }
    if (documentFiles.length === 0) {
      setErrors(prev => ({ ...prev, documentFiles: 'Please select at least one document file to upload' }));
      return;
    }

    setUploadLoading(true);
    setUploadErrors([]);
    setCurrentStep(4);
    try {
      const file = excelFiles[0].file;
      const documentFileNames = documentFiles.map(file => file.name);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            setErrors(prev => ({ ...prev, excelFile: 'Excel file is empty' }));
            setUploadLoading(false);
            return;
          }

          const validationError = validateExcelData(jsonData, documentFileNames);
          if (validationError) {
            setErrors(prev => ({ ...prev, excelFile: validationError }));
            setUploadLoading(false);
            return;
          }

          const validDocuments = jsonData.filter(row => 
            !row['Document Type']?.startsWith('Valid values:') &&
            !row['Category']?.startsWith('Valid values:') &&
            !row['Discipline']?.startsWith('Valid values:') &&
            row['Description'] !== 'Enter description here' &&
            row['Document Reference'] !== 'Enter reference number (must match uploaded file name without extension, e.g., "transmittal" for "transmittal.pdf")'
          );

          setUploadProgress({ current: 0, total: validDocuments.length });

          const chunkSize = 5;
          const errors = [];

          for (let i = 0; i < validDocuments.length; i += chunkSize) {
            const chunk = validDocuments.slice(i, i + chunkSize);
            const chunkFiles = chunk.map(doc => {
              const docRefWithoutExt = doc['Document Reference'].replace(/\.[^/.]+$/, '').trim().toLowerCase();
              return documentFiles.find(f => 
                f.name.replace(/\.[^/.]+$/, '').trim().toLowerCase() === docRefWithoutExt
              );
            }).filter(f => f);

            if (chunkFiles.length !== chunk.length) {
              errors.push(`Some documents in chunk starting at row ${i + 3} do not have matching files`);
              continue;
            }

            const formDataPayload = new FormData();
            chunkFiles.forEach((file, index) => {
              formDataPayload.append(`file`, file.file);
            });
            formDataPayload.append('data', JSON.stringify({
              documents: chunk,
              user,
              role: role?.trim(),
              projectName: JSON.parse(localStorage.getItem('Project'))
            }));

            try {
              const response = await axios.post('/bulk-upload-documents', formDataPayload, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
                timeout: 600000
              });

              setUploadProgress(prev => ({
                current: prev.current + chunk.length,
                total: prev.total
              }));

              if (response.data.errors && response.data.errors.length > 0) {
                errors.push(...response.data.errors.map(err => err.error));
              }
            } catch (err) {
              console.error('Chunk upload error:', err);
              errors.push(`Failed to upload chunk starting at document ${i + 1}: ${err.message}`);
            }

            await new Promise(resolve => setTimeout(resolve, 500));
          }

          setUploadErrors(errors);
          if (errors.length > 0) {
            setErrors(prev => ({
              ...prev,
              excelFile: `Some documents failed to upload: ${errors.join('; ')}`
            }));
            message.error('Some documents failed to upload. Check errors below.');
          } else {
            message.success('All documents uploaded successfully!');
            setExcelFiles([]);
            setDocumentFiles([]);
            setTimeout(() => {
              navigate('/home/view-document');
            }, 1000);
          }
        } catch (err) {
          console.error('Upload Error:', err);
          setErrors(prev => ({
            ...prev,
            excelFile: err.response?.data?.msg || 'Failed to upload Excel file'
          }));
        } finally {
          setUploadLoading(false);
          setUploadProgress({ current: 0, total: 0 });
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      setUploadLoading(false);
      setErrors(prev => ({ ...prev, excelFile: 'Failed to process Excel file' }));
      console.error('Upload Error:', err);
    }
  };

  const handleGoBack = () => {
    navigate('/home/upload-document');
  };

  const StepIndicator = ({ step, isActive, isCompleted }) => (
    <div className={`flex items-center ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
        isActive ? 'border-blue-600 bg-blue-50' : 
        isCompleted ? 'border-green-600 bg-green-50' : 
        'border-gray-300 bg-gray-50'
      }`}>
        {isCompleted ? (
          <CheckCircle className="w-6 h-6" />
        ) : (
          <step.icon className="w-6 h-6" />
        )}
      </div>
      <span className="ml-3 font-medium">{step.title}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button onClick={handleGoBack} className="p-2 hover:bg-white/50 rounded-full transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Bulk Document Upload</h1>
                <p className="text-gray-600 mt-1">Upload multiple documents efficiently with metadata</p>
              </div>
            </div>
          </div>

          {/* Step Progress */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <StepIndicator 
                    step={step} 
                    isActive={currentStep === step.id}
                    isCompleted={currentStep > step.id}
                  />
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 ${
                      currentStep > step.id ? 'bg-green-600' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Excel Template</h3>
                <div className="flex items-center space-x-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span>Ready to use</span>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Download the pre-configured template with all required fields and validation rules.
              </p>
              <Button
                type="primary"
                onClick={downloadExcelTemplate}
                size="large"
                disabled={isLoading}
                style={{
                  width: '100%',
                  height: '48px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                }}
              >
                <span className="flex items-center">
                  <Download className="w-5 h-5 mr-2" />
                  {isLoading ? 'Loading...' : 'Download Excel Template'}
                </span>
              </Button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Excel Metadata</h3>
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                  excelDragActive ? 'border-blue-500 bg-blue-50' : errors.excelFile ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onDragEnter={(e) => handleDragEvents(e, 'excel')}
                onDragLeave={(e) => handleDragEvents(e, 'excel')}
                onDragOver={(e) => handleDragEvents(e, 'excel')}
                onDrop={(e) => handleFileDrop(e, 'excel')}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleExcelFiles(e.target.files)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex flex-col items-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    excelDragActive ? 'bg-blue-100' : errors.excelFile ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    <FileSpreadsheet className={`w-8 h-8 ${
                      excelDragActive ? 'text-blue-600' : errors.excelFile ? 'text-red-500' : 'text-gray-400'
                    }`} />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    {excelDragActive ? 'Drop Excel file here' : 'Upload Excel file'}
                  </h4>
                  <p className="text-gray-500 mb-4">
                    Drag and drop your completed Excel file or click to browse
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <span>Supports:</span>
                    <span className="bg-gray-100 px-2 py-1 rounded">.xlsx</span>
                    <span className="bg-gray-100 px-2 py-1 rounded">.xls</span>
                  </div>
                </div>
              </div>

              {errors.excelFile && (
                <div className="flex items-center text-red-600 mt-2">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm">{errors.excelFile}</span>
                </div>
              )}

              {excelFiles.length > 0 && (
                <div className="mt-4">
                  {excelFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <FileSpreadsheet className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeExcelFile(file.id)}
                        className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Document Files</h3>
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                  documentDragActive ? 'border-blue-500 bg-blue-50' : errors.documentFiles ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onDragEnter={(e) => handleDragEvents(e, 'documents')}
                onDragLeave={(e) => handleDragEvents(e, 'documents')}
                onDragOver={(e) => handleDragEvents(e, 'documents')}
                onDrop={(e) => handleFileDrop(e, 'documents')}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => handleDocumentFiles(e.target.files)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex flex-col items-center">
                  <div className={`w-16 h-16 rounded 학교 flex items-center justify-center mb-4 ${
                    documentDragActive ? 'bg-blue-100' : errors.documentFiles ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    <Folder className={`w-8 h-8 ${
                      documentDragActive ? 'text-blue-600' : errors.documentFiles ? 'text-red-500' : 'text-gray-400'
                    }`} />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    {documentDragActive ? 'Drop documents here' : 'Upload document files'}
                  </h4>
                  <p className="text-gray-500 mb-4">
                    Drag and drop multiple files or click to browse
                  </p>
                </div>
              </div>

              {errors.documentFiles && (
                <div className="flex items-center text-red-600 mt-2">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm">{errors.documentFiles}</span>
                </div>
              )}

              {documentFiles.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">Selected Files ({documentFiles.length})</h4>
                    <button
                      onClick={() => setDocumentFiles([])}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {documentFiles.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <File className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeDocumentFile(file.id)}
                          className="w-6 h-6 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
                        >
                          <X className="w-3 h-3 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Ready to Upload</h3>
                  <p className="text-gray-600">
                    {excelFiles.length > 0 && documentFiles.length > 0
                      ? `${documentFiles.length} documents ready for processing`
                      : 'Please upload both Excel file and document files'}
                  </p>
                </div>
                <Button
                  type="primary"
                  onClick={handleUpload}
                  size="large"
                  disabled={isLoading || uploadLoading || excelFiles.length === 0 || documentFiles.length === 0}
                  style={{
                    padding: '12px 32px',
                    height: '48px',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    background: excelFiles.length > 0 && documentFiles.length > 0 && !uploadLoading
                      ? 'linear-gradient(to right, #16a34a, #15803d)'
                      : '',
                  }}
                >
                  <span className="flex items-center">
                    <Upload className="w-5 h-5 mr-2" />
                    {uploadLoading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}` : 'Start Upload'}
                  </span>
                </Button>
              </div>

              {uploadLoading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Uploading documents...</span>
                    <span className="text-sm font-medium text-blue-600">
                      {uploadProgress.current}/{uploadProgress.total}
                    </span>
                  </div>
                  <Progress
                    percent={Math.round((uploadProgress.current / uploadProgress.total) * 100)}
                    status={uploadErrors.length > 0 ? 'exception' : 'active'}
                  />
                </div>
              )}

              {uploadErrors.length > 0 && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Upload Errors</h3>
                  <ul className="list-disc list-inside text-red-600 space-y-2">
                    {uploadErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Summary</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Excel Files</span>
                  <span className="font-medium">{excelFiles.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Document Files</span>
                  <span className="font-medium">{documentFiles.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Size</span>
                  <span className="font-medium">
                    {formatFileSize([...excelFiles, ...documentFiles].reduce((acc, file) => acc + file.size, 0))}
                  </span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      excelFiles.length > 0 && documentFiles.length > 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {excelFiles.length > 0 && documentFiles.length > 0 ? 'Ready' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-blue-900">Quick Guide</h3>
              </div>
              <div className="space-y-3 text-sm text-blue-800">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-blue-600">1</span>
                  </div>
                  <span>Download the Excel template using the Download Excel Template button above.</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-blue-600">2</span>
                  </div>
                  <span>Fill in the required fields: Document Type, Category, Discipline, Description, and Document Reference.</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-blue-600">3</span>
                  </div>
                  <span>Upload the completed Excel file.</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-blue-600">4</span>
                  </div>
                  <span>Ensure each Document Reference matches the name of an uploaded document file.</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-blue-600">5</span>
                  </div>
                  <span>Review and start the bulk upload process.</span>
                </div>
              </div>
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="text-red-800 font-semibold">Error</p>
                    <p className="text-red-600 text-sm">{errors.general}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;